// Cierge Labs v1 — Receipt shapes for the side-by-side preview.
//
// Two distinct objects are produced:
//   1. synthetic — exactly what v1 actually persists today
//   2. illustrative — a faithful mock of the structure a real
//      KATLAS-issued signed receipt would carry. Clearly tagged.
//
// If a wallet is present, the *signature* field on the illustrative
// receipt is genuinely produced by the visitor's WebCrypto private
// key over the canonical payload. Issuer DID, authority, and
// expiry remain illustrative.

import { signPayload } from "./wallet";

const SECONDS_IN_DAY = 86400;

const buf2hex = (buf) =>
  [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");

async function sha256Hex(text) {
  const data = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return buf2hex(digest);
}

function selectionSummary(sel) {
  return [
    sel.user_type?.label,
    sel.sector?.label,
    sel.problem?.label,
    sel.scope?.label,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function buildSyntheticReceipt({ savedDraft, output, wallet }) {
  return {
    _label: "synthetic",
    kind: "cierge_labs.v1.synthetic",
    id: savedDraft?.id
      ? `rcpt_${savedDraft.id.slice(0, 12)}`
      : "rcpt_unsaved_preview",
    issued_at: savedDraft?.created_at || new Date().toISOString(),
    wallet_fingerprint: wallet ? wallet.fingerprint : null,
    selection_summary: selectionSummary({
      user_type: output.selection.user_type,
      sector: output.selection.sector,
      problem: output.selection.problem,
      scope: output.selection.scope,
    }),
    headline: output.headline,
    cryptographically_signed: false,
    note: "v1 places no signature on this receipt. It is captured for continuity only.",
  };
}

export async function buildIllustrativeReceipt({ savedDraft, output, wallet }) {
  const now = new Date();
  const expires = new Date(now.getTime() + 30 * SECONDS_IN_DAY * 1000);
  const issuerDid = "did:katlas:illustrative:cierge-labs-v1";

  const payload = {
    "@context": ["https://katlas.org/ns/governance/v1"],
    type: ["KatlasReceipt", "GovernanceReceipt"],
    issuer: issuerDid,
    issued_at: now.toISOString(),
    expires_at: expires.toISOString(),
    purpose: "shape_use_case",
    authority: {
      role_id: output.selection.user_type.id,
      role_label: output.selection.user_type.label,
      mandate: "synthetic-shaping-only",
    },
    subject: {
      wallet_fingerprint: wallet ? wallet.fingerprint : null,
      handle: wallet ? wallet.handle : null,
    },
    governance_scope: {
      id: output.selection.scope.id,
      label: output.selection.scope.label,
    },
    workload: {
      sector: output.selection.sector.id,
      problem_class: output.selection.problem.id,
      headline: output.headline,
    },
    references: {
      draft_id: savedDraft?.id || null,
    },
  };

  const canonical = JSON.stringify(payload);
  const content_hash = `sha256:${await sha256Hex(canonical)}`;

  let signature = {
    alg: "ES256",
    value: null,
    signed: false,
    over: "sha256(canonical_payload)",
    note: "Illustrative — no wallet present to sign over the payload.",
  };

  if (wallet) {
    try {
      const sigHex = await signPayload(content_hash);
      signature = {
        alg: "ES256",
        value: sigHex,
        signed: true,
        over: "sha256(canonical_payload)",
        note: "Signed by visitor's WebCrypto wallet. Issuer DID and authority remain illustrative — KATLAS would issue these in production.",
      };
    } catch (e) {
      // leave unsigned
    }
  }

  return {
    _label: "illustrative",
    kind: "katlas.governance_receipt.illustrative",
    payload,
    content_hash,
    signature,
    illustrative: true,
    note: "This shape is illustrative of what KATLAS would issue. Cierge Labs v1 does NOT issue this receipt today.",
  };
}
